import React, { useState, useCallback } from 'react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { ShippingAddress } from '../types/Shipping';
import { AUSTRALIAN_STATES, calculateShipping, FREE_SHIPPING_THRESHOLD } from '../utils/shippingRates';
import { validateShippingForm, formatPhoneNumber, sanitizeInput } from '../utils/validation';
import './styles/CheckoutPage.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_YOUR_KEY');

type CheckoutStep = 'shipping' | 'payment';

const CheckoutPage: React.FC = () => {
  const { cart, cartTotal } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState<CheckoutStep>('shipping');
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    fullName: '',
    email: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postcode: '',
    country: 'AU'
  });
  const [shippingMethod, setShippingMethod] = useState<'standard' | 'express'>('standard');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string>('');

  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:4242';

  // Check if shipping address is complete enough to calculate shipping
  const hasShippingAddress = shippingAddress.postcode.trim() !== '' && shippingAddress.state !== '';

  // Calculate shipping cost (only if address provided)
  const shippingCost = hasShippingAddress 
    ? calculateShipping(shippingAddress.state, shippingMethod, cartTotal)
    : 0;
  const orderTotal = hasShippingAddress 
    ? cartTotal + shippingCost
    : cartTotal;

  // Handle shipping form submission
  const handleContinueToPayment = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setFormErrors({});
    setApiError('');
    
    // Validate form using improved validation
    const errors = validateShippingForm(shippingAddress);
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      document.getElementById(firstErrorField)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setStep('payment');
  };

  // Handle phone input with formatting
  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    setShippingAddress({ ...shippingAddress, phone: formatted });
  };

  // Fetch client secret for Stripe
  const fetchClientSecret = useCallback(async () => {
    setApiError('');

    try {
      const response = await fetch(`${API_ENDPOINT}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lineItems: cart.map(item => ({
            id: item.id,              // Backend validates price from this ID
            quantity: item.quantity,  // Backend validates this is reasonable
          })),
          shipping: {
            name: sanitizeInput(shippingAddress.fullName),
            address: {
              line1: sanitizeInput(shippingAddress.addressLine1),
              line2: sanitizeInput(shippingAddress.addressLine2 || ''),
              city: sanitizeInput(shippingAddress.city),
              state: shippingAddress.state,
              postal_code: shippingAddress.postcode,
              country: shippingAddress.country,
            },
            cost: shippingCost,      // Backend validates this matches state
            method: shippingMethod,
          },
          customerEmail: shippingAddress.email.trim().toLowerCase(),
          metadata: {
            source: 'maple-nook-website',
            itemCount: cart.length,
            shippingMethod: shippingMethod,
          },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle specific error messages from backend
        throw new Error(data.error || 'Failed to create checkout session');
      }

      return data.clientSecret;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unable to process checkout';
      setApiError(errorMessage);
      
      // Go back to shipping step so user can see the error
      setStep('shipping');
      
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      throw error;
    }
  }, [cart, API_ENDPOINT, shippingAddress, shippingCost, shippingMethod]);

  // Empty cart handler
  if (cart.length === 0) {
    return (
      <div className="checkout-page">
        <div className="empty-cart">
          <svg className="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h1 className="empty-title font-serif">Your cart is empty</h1>
          <p className="empty-message">Add some cozy items to get started!</p>
          <button onClick={() => navigate('/')} className="continue-shopping">Browse Products</button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      {/* Progress Indicator */}
      <div className="checkout-progress">
        <div className={`progress-step ${step === 'shipping' ? 'active' : 'completed'}`}>
          <div className="step-number">1</div>
          <div className="step-label">Shipping</div>
        </div>
        <div className="progress-line"></div>
        <div className={`progress-step ${step === 'payment' ? 'active' : ''}`}>
          <div className="step-number">2</div>
          <div className="step-label">Payment</div>
        </div>
      </div>

      <h1 className="page-title font-serif">Checkout</h1>

      {/* STEP 1: SHIPPING */}
      {step === 'shipping' && (
        <div className="checkout-grid">
          {/* Order Summary (Read-only) */}
          <div className="order-summary-section">
            <h2 className="section-title font-serif">Order Summary</h2>
            
            <div className="summary-items">
              {cart.map((item) => (
                <div key={item.id} className="summary-item">
                  <img src={item.image} alt={item.name} className="summary-item-image" />
                  <div className="summary-item-details">
                    <h3 className="summary-item-name">{item.name}</h3>
                    <p className="summary-item-quantity">Qty: {item.quantity}</p>
                  </div>
                  <div className="summary-item-price">${(item.price * item.quantity).toFixed(2)}</div>
                </div>
              ))}
            </div>

            <div className="summary-totals">
              <div className="summary-row">
                <span>Subtotal:</span>
                <span>${cartTotal.toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span>Shipping ({shippingMethod}):</span>
                <span>
                  {!hasShippingAddress ? (
                    <span className="shipping-pending">
                      <span className="blur-placeholder">$8.00</span>
                    </span>
                  ) : shippingCost === 0 ? (
                    <span className="free-shipping">FREE</span>
                  ) : (
                    `$${shippingCost.toFixed(2)}`
                  )}
                </span>
              </div>
              {!hasShippingAddress && (
                <div className="shipping-notice">
                  💡 Shipping calculated after address is entered
                </div>
              )}
              {hasShippingAddress && cartTotal >= FREE_SHIPPING_THRESHOLD && shippingMethod === 'standard' && (
                <div className="free-shipping-notice">
                  🎉 You qualify for free standard shipping!
                </div>
              )}
              <div className="summary-row total">
                <span>Total:</span>
                <span>
                  {!hasShippingAddress ? (
                    <span className="shipping-pending">
                      <span className="blur-placeholder">${(cartTotal + 8).toFixed(2)}</span>
                    </span>
                  ) : (
                    `$${orderTotal.toFixed(2)}`
                  )}
                </span>
              </div>
            </div>

            <button onClick={() => navigate('/cart')} className="edit-cart-button">
              ← Edit Cart
            </button>
          </div>

          {/* Shipping Form */}
          <div className="shipping-form-section">
            <h2 className="section-title font-serif">Shipping Details</h2>
            
            {/* API Error Message */}
            {apiError && (
              <div className="api-error-banner">
                <svg className="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="error-content">
                  <strong>Checkout Error</strong>
                  <p>{apiError}</p>
                </div>
              </div>
            )}
            
            <form onSubmit={handleContinueToPayment} className="shipping-form">
              {/* Contact Information */}
              <div className="form-section">
                <h3 className="form-section-title">Contact Information</h3>
                
                <div className="form-group">
                  <label htmlFor="fullName">Full Name *</label>
                  <input
                    type="text"
                    id="fullName"
                    value={shippingAddress.fullName}
                    onChange={(e) => setShippingAddress({...shippingAddress, fullName: e.target.value})}
                    className={formErrors.fullName ? 'error' : ''}
                    maxLength={100}
                  />
                  {formErrors.fullName && <span className="error-message">{formErrors.fullName}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      value={shippingAddress.email}
                      onChange={(e) => setShippingAddress({...shippingAddress, email: e.target.value})}
                      className={formErrors.email ? 'error' : ''}
                      maxLength={320}
                      autoComplete="email"
                    />
                    {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone *</label>
                    <input
                      type="tel"
                      id="phone"
                      placeholder="04XX XXX XXX"
                      value={shippingAddress.phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      className={formErrors.phone ? 'error' : ''}
                      maxLength={15}
                      autoComplete="tel"
                    />
                    {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="form-section">
                <h3 className="form-section-title">Shipping Address</h3>
                
                <div className="form-group">
                  <label htmlFor="addressLine1">Street Address *</label>
                  <input
                    type="text"
                    id="addressLine1"
                    placeholder="123 Main Street"
                    value={shippingAddress.addressLine1}
                    onChange={(e) => setShippingAddress({...shippingAddress, addressLine1: e.target.value})}
                    className={formErrors.addressLine1 ? 'error' : ''}
                    maxLength={100}
                    autoComplete="address-line1"
                  />
                  {formErrors.addressLine1 && <span className="error-message">{formErrors.addressLine1}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="addressLine2">Apartment, Suite, etc. (optional)</label>
                  <input
                    type="text"
                    id="addressLine2"
                    placeholder="Apt 4B"
                    value={shippingAddress.addressLine2}
                    onChange={(e) => setShippingAddress({...shippingAddress, addressLine2: e.target.value})}
                    maxLength={100}
                    autoComplete="address-line2"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">City *</label>
                    <input
                      type="text"
                      id="city"
                      value={shippingAddress.city}
                      onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                      className={formErrors.city ? 'error' : ''}
                      maxLength={50}
                      autoComplete="address-level2"
                    />
                    {formErrors.city && <span className="error-message">{formErrors.city}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="state">State *</label>
                    <select
                      id="state"
                      value={shippingAddress.state}
                      onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})}
                      autoComplete="address-level1"
                    >
                      <option value="">Select state...</option>
                      {AUSTRALIAN_STATES.map(state => (
                        <option key={state.value} value={state.value}>{state.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="postcode">Postcode *</label>
                    <input
                      type="text"
                      id="postcode"
                      placeholder="2000"
                      maxLength={4}
                      value={shippingAddress.postcode}
                      onChange={(e) => setShippingAddress({...shippingAddress, postcode: e.target.value.replace(/\D/g, '')})}
                      className={formErrors.postcode ? 'error' : ''}
                      autoComplete="postal-code"
                    />
                    {formErrors.postcode && <span className="error-message">{formErrors.postcode}</span>}
                  </div>
                </div>
              </div>

              {/* Shipping Method */}
              <div className="form-section">
                <h3 className="form-section-title">Shipping Method</h3>
                
                <div className="shipping-options">
                  <label className={`shipping-option ${shippingMethod === 'standard' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="shippingMethod"
                      value="standard"
                      checked={shippingMethod === 'standard'}
                      onChange={(e) => setShippingMethod(e.target.value as 'standard')}
                    />
                    <div className="option-details">
                      <div className="option-name">Standard Shipping</div>
                      <div className="option-time">5-7 business days</div>
                    </div>
                    <div className="option-price">
                      {!hasShippingAddress ? (
                        <span className="blur-placeholder">$8.00</span>
                      ) : calculateShipping(shippingAddress.state, 'standard', cartTotal) === 0 ? (
                        <span className="free-badge">FREE</span>
                      ) : (
                        `$${calculateShipping(shippingAddress.state, 'standard', cartTotal).toFixed(2)}`
                      )}
                    </div>
                  </label>

                  <label className={`shipping-option ${shippingMethod === 'express' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="shippingMethod"
                      value="express"
                      checked={shippingMethod === 'express'}
                      onChange={(e) => setShippingMethod(e.target.value as 'express')}
                    />
                    <div className="option-details">
                      <div className="option-name">Express Shipping</div>
                      <div className="option-time">2-3 business days</div>
                    </div>
                    <div className="option-price">
                      {!hasShippingAddress ? (
                        <span className="blur-placeholder">$15.00</span>
                      ) : (
                        `$${calculateShipping(shippingAddress.state, 'express', cartTotal).toFixed(2)}`
                      )}
                    </div>
                  </label>
                </div>
              </div>

              <button type="submit" className="continue-button">
                Continue to Payment
                <svg className="button-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </form>
          </div>
        </div>
      )}

      {/* STEP 2: PAYMENT */}
      {step === 'payment' && (
        <div className="checkout-grid payment-step">
          {/* Order Summary (Locked) */}
          <div className="order-summary-section locked">
            <h2 className="section-title font-serif">Order Summary</h2>
            
            <div className="summary-items">
              {cart.map((item) => (
                <div key={item.id} className="summary-item">
                  <img src={item.image} alt={item.name} className="summary-item-image" />
                  <div className="summary-item-details">
                    <h3 className="summary-item-name">{item.name}</h3>
                    <p className="summary-item-quantity">Qty: {item.quantity}</p>
                  </div>
                  <div className="summary-item-price">${(item.price * item.quantity).toFixed(2)}</div>
                </div>
              ))}
            </div>

            <div className="shipping-info">
              <h3 className="info-title">Shipping To:</h3>
              <p className="info-text">{shippingAddress.fullName}</p>
              <p className="info-text">{shippingAddress.addressLine1}</p>
              {shippingAddress.addressLine2 && <p className="info-text">{shippingAddress.addressLine2}</p>}
              <p className="info-text">{shippingAddress.city}, {shippingAddress.state} {shippingAddress.postcode}</p>
              <button onClick={() => setStep('shipping')} className="edit-link">Edit</button>
            </div>

            <div className="summary-totals">
              <div className="summary-row">
                <span>Subtotal:</span>
                <span>${cartTotal.toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span>Shipping ({shippingMethod}):</span>
                <span>
                  {shippingCost === 0 ? (
                    <span className="free-shipping">FREE</span>
                  ) : (
                    `$${shippingCost.toFixed(2)}`
                  )}
                </span>
              </div>
              <div className="summary-row total">
                <span>Total:</span>
                <span>${orderTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Stripe Payment */}
          <div className="payment-section">
            <div className="payment-header">
              <button onClick={() => setStep('shipping')} className="back-button">
                <svg className="back-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Shipping
              </button>
              <h2 className="section-title font-serif">Payment Details</h2>
            </div>
            
            <div id="checkout">
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{ fetchClientSecret }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutPage;